import api, { handleApiResponse, handleApiError, tokenManager } from './api';

class AuthService {
  // Login user - AuthContext pattern
  async login(email, password) {
  try {
    console.log('AuthService login - params:', { email: email, password: '***' });
    
    const response = await api.post('/auth/login', { email, password });
    const responseData = handleApiResponse(response);
    
    console.log('AuthService login - response:', responseData);
    
    const { data } = responseData;
    
    // Store tokens with expiration info
    if (data.access && data.refresh) {
      tokenManager.setTokens(data.access, data.refresh);
      sessionStorage.setItem('user', JSON.stringify(data.user));
      sessionStorage.setItem('role', data.role || 'CUSTOMER');
      
      // Store expiration info for auto-logout
      if (data.expires_at) {
        sessionStorage.setItem('token_expires_at', data.expires_at);
      }
      if (data.expires_in) {
        sessionStorage.setItem('token_expires_in', data.expires_in.toString());
      }
      
      // Setup auto-logout after successful login
      this.setupAutoLogout();
    }
    
    return responseData;
  } catch (error) {
    console.error('AuthService login error:', error);
    
    // Better error handling
    if (error.response?.status === 401) {
      throw new Error('Invalid email or password. Please check your credentials.');
    } else if (error.response?.status === 403) {
      throw new Error('Your account has been deactivated. Please contact support.');
    } else if (error.response?.status === 503) {
      throw new Error('System is under maintenance. Please try again later.');
    } else if (error.response?.status === 422) {
      const validationErrors = error.response.data?.errors;
      if (validationErrors) {
        const firstError = Object.values(validationErrors)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : firstError);
      }
      throw new Error('Please check your input and try again.');
    }
    
    throw new Error(handleApiError(error) || 'Login failed. Please try again.');
  }
}

setupAutoLogout() {
  const expiresAt = sessionStorage.getItem('token_expires_at');
  if (!expiresAt) return;
  
  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const timeUntilExpiry = expirationDate.getTime() - now.getTime();
  
  if (timeUntilExpiry > 0) {
    // Set timeout to logout 5 minutes before actual expiration
    const logoutTime = Math.max(timeUntilExpiry - 300000, 30000); // At least 30 seconds
    
    console.log(`Auto-logout scheduled in ${Math.round(logoutTime / 1000)} seconds`);
    
    setTimeout(() => {
      console.log('Auto-logout triggered');
      this.logout();
      // Show notification to user
      if (typeof window !== 'undefined' && window.showToast) {
        window.showToast('Your session has expired. Please login again.', 'warning');
      }
    }, logoutTime);
  } else {
    // Token is already expired
    console.log('Token already expired, logging out immediately');
    this.logout();
  }
}


  // Get current user - Added alias for AuthContext compatibility
  async getUser() {
    return this.getCurrentUser();
  }

  // Get current user
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/user');
      const responseData = handleApiResponse(response);
      
      console.log('AuthService getCurrentUser - full response:', responseData);
      
      // Extract the actual data from the nested structure
      const { data } = responseData;
      
      // Enhanced user data extraction with permissions
      if (data.user) {
        const enhancedUser = {
          ...data.user,
          // Ensure roles and permissions are arrays
          roles: data.roles || data.user.roles || [],
          permissions: data.permissions || data.user.permissions || []
        };
        
        console.log('AuthService - enhanced user with permissions:', enhancedUser);
        
        // FIXED: Use sessionStorage
        sessionStorage.setItem('user', JSON.stringify(enhancedUser));
        if (data.role) {
          sessionStorage.setItem('role', data.role);
        }
      }
      
      return responseData;
    } catch (error) {
      console.error('AuthService getCurrentUser error:', error);
      throw new Error(handleApiError(error));
    }
  }

  // Register new user
  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      const responseData = handleApiResponse(response);
      
      // Extract the actual data from the nested structure
      const { data } = responseData;
      
      // Auto-login after registration - FIXED: Use sessionStorage
      if (data.access && data.refresh) {
        tokenManager.setTokens(data.access, data.refresh);
        sessionStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('role', data.role || 'CUSTOMER');
      }
      
      return responseData;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Logout user
async logout() {
  try {
    if (tokenManager.getAccessToken()) {
      await api.post('/auth/logout');
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Complete cleanup of all stored data
    tokenManager.clearTokens();
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('token_expires_in');
    
    // Clear any cached data
    sessionStorage.clear();
    
    // Reload page to ensure clean state
    window.location.reload();
  }
}

  // Change password
  async changePassword(currentPassword, newPassword, newPasswordConfirmation) {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirmation
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Reset password with OTP
  async resetPassword(token, otp, password, passwordConfirmation) {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        otp,
        password,
        password_confirmation: passwordConfirmation
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update profile
  async updateProfile(profileData) {
    try {
      const response = await api.patch('/auth/profile', profileData);
      const responseData = handleApiResponse(response);
      
      // Extract the actual data from the nested structure
      const { data } = responseData;
      
      // Update stored user data - FIXED: Use sessionStorage
      const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      const updatedUser = { ...currentUser, ...data.user };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      
      return responseData;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Upload profile image
  async uploadProfileImage(file) {
    try {
      const formData = new FormData();
      formData.append('profile_image', file);
      
      const response = await api.post('/auth/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Delete profile image
  async deleteProfileImage() {
    try {
      const response = await api.delete('/auth/profile/image');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Check if user is authenticated
isAuthenticated() {
  const token = tokenManager.getAccessToken();
  if (!token) return false;
  
  // Check if token is expired
  const expiresAt = sessionStorage.getItem('token_expires_at');
  if (expiresAt) {
    const expirationDate = new Date(expiresAt);
    if (new Date() >= expirationDate) {
      console.log('Token expired, logging out');
      this.logout();
      return false;
    }
  }
  
  return true;
}

  // Get stored user - FIXED: Use sessionStorage
  getStoredUser() {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get stored role - FIXED: Use sessionStorage
  getStoredRole() {
    return sessionStorage.getItem('role') || 'CUSTOMER';
  }

  // Check if user has permission
  hasPermission(permission) {
    const user = this.getStoredUser();
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }

  // Check if user has role
  hasRole(role) {
    const user = this.getStoredUser();
    if (!user || !user.roles) return false;
    return user.roles.some(userRole => {
      if (typeof userRole === 'string') {
        return userRole.toLowerCase() === role.toLowerCase();
      }
      return userRole.name && userRole.name.toLowerCase() === role.toLowerCase();
    });
  }

  // Check if user is admin
  isAdmin() {
    const role = this.getStoredRole();
    return role === 'ADMIN' || this.hasRole('admin') || this.hasRole('administrator');
  }

  // Check if user is agent
  isAgent() {
    const user = this.getStoredUser();
    return user?.is_agent || this.hasRole('agent') || this.hasRole('support-agent');
  }

  // Check if user is customer care
  isCustomerCare() {
    return this.hasRole('customer-care') || this.hasRole('customer-service');
  }

  // Check if user is customer
  isCustomer() {
    const role = this.getStoredRole();
    return role === 'CUSTOMER' || (!this.isAdmin() && !this.isAgent() && !this.isCustomerCare());
  }
}

export default new AuthService();